import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import EquationInput from "../../../../components/inputs/EquationInput";
import { scoreStaged } from "../../../openTaskScoring";

const CASES = [
  { aToB: 80, bToC: 120, item: "hulajnogi", shopA: "A", shopB: "B", shopC: "C" },
  { aToB: 70, bToC: 130, item: "plecaka", shopA: "A", shopB: "B", shopC: "C" },
  { aToB: 90, bToC: 105, item: "kasku", shopA: "A", shopB: "B", shopC: "C" },
  { aToB: 75, bToC: 125, item: "roweru", shopA: "A", shopB: "B", shopC: "C" },
  { aToB: 60, bToC: 150, item: "zegarka", shopA: "A", shopB: "B", shopC: "C" },
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function factor(percent) {
  return percent / 100;
}

function formatDecimal(value) {
  return Number(value.toFixed(4)).toString().replace(".", ",");
}

function ShopLabel({ value }) {
  return <InlineMath math={value} />;
}

function buildData(config) {
  const bFactor = factor(config.aToB);
  const cFactor = factor(config.bToC) * bFactor;
  return {
    ...config,
    bFactor,
    cFactor,
    cPercentOfA: Math.round(cFactor * 10000) / 100,
    maxPoints: 2,
  };
}

function createOriginalTask() {
  return {
    type: "E8ScooterPricePercentProofOpenTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 16 },
    tags: ["e8", "procenty", "porownania-procentowe", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8ScooterPricePercentProofOpenTask",
    data: buildData(pick(CASES.slice(1))),
    source: { variant: "random", basedOn: "E8_2025_CZE_16" },
    tags: ["e8", "procenty", "porownania-procentowe", "zadanie-otwarte"],
  };
}

function normalize(raw) {
  return String(raw || "")
    .toLowerCase()
    .replace(/,/g, ".")
    .replace(/\\cdot|·|\*/g, "*")
    .replace(/\s+/g, "");
}

function numberTokens(raw) {
  return (normalize(raw).match(/\d+(?:\.\d+)?/g) || []).map(Number);
}

function hasApprox(raw, value, tolerance = 0.001) {
  return numberTokens(raw).some((token) => Math.abs(token - value) <= tolerance);
}

function relationLooksCorrect(raw, data) {
  const text = normalize(raw);
  if (!text || !text.includes("x")) return false;
  const hasB = hasApprox(text, data.bFactor) || text.includes(`${data.aToB}%`);
  const hasMultiplier = hasApprox(text, factor(data.bToC)) || text.includes(`${data.bToC}%`);
  const hasC = hasApprox(text, data.cFactor) || hasApprox(text, data.cPercentOfA);
  return hasB && hasMultiplier && hasC;
}

function conclusionLooksCorrect(raw, data) {
  const text = normalize(raw);
  if (!text) return false;
  const saysLower =
    text.includes("<x") ||
    text.includes("mniejsz") ||
    text.includes("nizsz") ||
    text.includes("niższ") ||
    text.includes(`${100 - data.cPercentOfA}%`);
  return saysLower && (hasApprox(text, data.cFactor) || hasApprox(text, data.cPercentOfA));
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Takie same {data.item} są sprzedawane w sklepach <ShopLabel value={data.shopA} />, <ShopLabel value={data.shopB} />, <ShopLabel value={data.shopC} />.
        W sklepie <ShopLabel value={data.shopB} /> cena {data.item} stanowi <InlineMath math={`${data.aToB}\\%`} /> ceny
        {data.item} w sklepie <ShopLabel value={data.shopA} />. W sklepie <ShopLabel value={data.shopC} /> cena {data.item} stanowi{" "}
        <InlineMath math={`${data.bToC}\\%`} /> ceny {data.item} w sklepie <ShopLabel value={data.shopB} />.
      </p>
      <p>Uzasadnij, że cena {data.item} w sklepie <ShopLabel value={data.shopC} /> jest niższa od ceny {data.item} w sklepie <ShopLabel value={data.shopA} />. Zapisz obliczenia.</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 1. Zapisz cenę w sklepie C w zależności od ceny w sklepie A.</div>
        <EquationInput
          value={userAnswer.relation || ""}
          onChange={setField("relation")}
          disabled={disabled}
          placeholder={`np. ${formatDecimal(factor(data.bToC))}*${formatDecimal(data.bFactor)}x=${formatDecimal(data.cFactor)}x`}
          showPreview
          basis="420px"
        />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 2. Zapisz wniosek.</div>
        <EquationInput
          value={userAnswer.conclusion || ""}
          onChange={setField("conclusion")}
          disabled={disabled}
          placeholder={`np. ${formatDecimal(data.cFactor)}x < x, więc cena jest niższa`}
          showPreview={false}
          basis="520px"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const relationOk = relationLooksCorrect(userAnswer.relation, data);
  const conclusionOk = conclusionLooksCorrect(userAnswer.conclusion, data);
  return scoreStaged({
    resultOk: conclusionOk,
    milestones: [relationOk],
    maxScore: data.maxPoints,
    parts: {
      "zale\u017cno\u015b\u0107 mi\u0119dzy cenami": relationOk,
      "wniosek": conclusionOk,
    },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`${data.shopB}:\\ ${formatDecimal(data.bFactor)}x`} />
      <BlockMath math={`${data.shopC}:\\ ${formatDecimal(factor(data.bToC))}\\cdot ${formatDecimal(data.bFactor)}x=${formatDecimal(data.cFactor)}x`} />
      <BlockMath math={`${formatDecimal(data.cFactor)}x<x`} />
      <div>{"Odpowied\u017a: cena jest ni\u017csza od ceny w sklepie "}<ShopLabel value={data.shopA} />{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>Zależność: {userAnswer.relation || <i>(brak)</i>}</div>
      <div>Wniosek: {userAnswer.conclusion || <i>(brak)</i>}</div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Oznaczmy cenę {data.item} w sklepie <ShopLabel value={data.shopA} /> przez <InlineMath math="x" />.</p>
      <BlockMath math={`${data.shopB}:\\ ${formatDecimal(data.bFactor)}x`} />
      <p><strong>Krok 2.</strong> Cena w sklepie <ShopLabel value={data.shopC} /> stanowi <InlineMath math={`${data.bToC}\\%`} /> ceny ze sklepu <ShopLabel value={data.shopB} />, więc:</p>
      <BlockMath math={`${data.shopC}:\\ ${formatDecimal(factor(data.bToC))}\\cdot ${formatDecimal(data.bFactor)}x=${formatDecimal(data.cFactor)}x`} />
      <p><strong>Krok 3.</strong> Porównujemy z ceną w sklepie <ShopLabel value={data.shopA} />.</p>
      <BlockMath math={`${formatDecimal(data.cFactor)}x < x`} />
      <p>Cena {data.item} w sklepie <ShopLabel value={data.shopC} /> jest niższa od ceny {data.item} w sklepie <ShopLabel value={data.shopA} />.</p>
    </div>
  );
}